import { useState } from 'react';
import { ChevronLeft, ChevronRight, ShoppingBag } from 'lucide-react';
import type { StorefrontProduct } from '../api/client';
import { useListProducts } from '../elements/render/useStoreCatalog';
import { cartMoney } from './cartMoney';
import type { Commerce } from './ShopCommerce';
import { shopWords } from './shopWords';
import { responsive, THUMB_SIZES } from '../elements/render/responsiveImage';
import { named, shopNames } from './shopNames';

// Sleek's "You may also like" under the cart lines: the store's best sellers that are not in the cart yet,
// one at a time with a 1/2 pager, each with a bag that adds it — straight away for a product with one
// option, through the quick view for one with several, where the option is chosen.

/** How many best sellers are read, before those already in the cart are left out. */
const READ = 6;

// Adds a one-option product at once, or opens the quick view on one whose option must be chosen first.
function addOrChoose(commerce: Commerce, product: StorefrontProduct): void {
  const only = product.variants.length === 1 ? product.variants[0] : null;
  if (only) return commerce.add(product, only.id, 1);
  commerce.peek(product);
}

// Draws one suggestion: the picture on a tile, its type, name and price, and the bag that adds it.
const Suggestion: React.FC<{ product: StorefrontProduct; commerce: Commerce }> = ({ product, commerce }) => {
  const price = Number(product.variants[0]?.price ?? 0);
  return (
    <div className="xe-cart-suggestion">
      <a className="xe-cart-suggestion-media" href={`/products/${encodeURIComponent(product.slug)}`} tabIndex={-1} aria-hidden="true">
        {product.images[0] ? <img {...responsive(product.images[0].src, THUMB_SIZES)} alt="" loading="lazy" /> : null}
      </a>
      <div className="xe-cart-suggestion-words">
        {product.productType ? <span className="xe-cart-suggestion-type">{product.productType}</span> : null}
        <a href={`/products/${encodeURIComponent(product.slug)}`} dir="auto">{product.title}</a>
        <span>{cartMoney(price, commerce.currency, commerce.direction)}</span>
      </div>
      <button type="button" className="xe-cart-suggestion-add" aria-label={named(shopNames(commerce.language).add, { title: product.title })} onClick={() => addOrChoose(commerce, product)}>
        <ShoppingBag size={18} strokeWidth={1.5} aria-hidden="true" />
      </button>
    </div>
  );
};

// Draws the suggestions with their pager, or nothing when every best seller is already in the cart.
export const CartSuggestions: React.FC<{ commerce: Commerce }> = ({ commerce }) => {
  const read = useListProducts({ kind: 'best-selling' }, READ);
  const [at, setAt] = useState(0);
  const inCart = new Set(commerce.lines.map((line) => line.slug));
  const rows = (read.data ?? []).filter((row) => !inCart.has(row.slug) && row.variants.length > 0);
  if (rows.length === 0) return null;
  const shown = Math.min(at, rows.length - 1);
  return (
    <section className="xe-cart-suggestions" aria-label={shopWords(commerce.language).youMayAlsoLike}>
      <div className="xe-cart-suggestions-head">
        <h3>{shopWords(commerce.language).youMayAlsoLike}</h3>
        {rows.length > 1 ? (
          <div className="xe-cart-suggestions-pager">
            <button type="button" aria-label={shopNames(commerce.language).previous} disabled={shown === 0} onClick={() => setAt(shown - 1)}><ChevronLeft size={16} aria-hidden="true" /></button>
            <span>{shown + 1}/{rows.length}</span>
            <button type="button" aria-label={shopNames(commerce.language).next} disabled={shown === rows.length - 1} onClick={() => setAt(shown + 1)}><ChevronRight size={16} aria-hidden="true" /></button>
          </div>
        ) : null}
      </div>
      <Suggestion product={rows[shown]} commerce={commerce} />
    </section>
  );
};
